// Theme-aware Recharts tooltip. Reads CSS variables defined in
// styles/trends.css so light/dark mode switches without remounting the
// chart. Drop in as `<Tooltip content={<ChartTooltip />} />`.
import type { TooltipProps } from 'recharts';

type Payload = NonNullable<TooltipProps<number | string, string>['payload']>;

interface ExtraProps {
  // When true, show only entries with numeric values > 0. Stacked charts
  // accumulate zero-valued series for layout stability — those rows would
  // otherwise clutter the tooltip body.
  hideZero?: boolean;
  valueFormatter?: (v: number | string, name?: string) => string;
  labelFormatter?: (
    label: string | number | undefined,
    payload: Payload,
  ) => string;
  // When true (default), each row shows series-name + value. When false,
  // hides the per-series name (useful for single-series sparkline tooltips).
  showName?: boolean;
}

export default function ChartTooltip(
  props: TooltipProps<number | string, string> & ExtraProps,
) {
  const {
    active,
    payload,
    label,
    hideZero = true,
    valueFormatter,
    labelFormatter,
    showName = true,
  } = props;
  if (!active || !payload || payload.length === 0) return null;

  const entries = (payload as Payload).filter((p) => {
    if (!hideZero) return true;
    const v = typeof p.value === 'number' ? p.value : Number(p.value);
    return Number.isFinite(v) && v !== 0;
  });
  if (entries.length === 0) return null;

  const fmt = (v: number | string | undefined, name?: string): string => {
    if (v == null) return '';
    if (valueFormatter) return valueFormatter(v, name);
    if (typeof v === 'number') return v.toLocaleString();
    return String(v);
  };

  const resolvedLabel = labelFormatter
    ? labelFormatter(label as string | number | undefined, payload as Payload)
    : label;

  return (
    <div className="chart-tooltip" role="tooltip">
      {resolvedLabel != null && resolvedLabel !== '' && (
        <div className="chart-tooltip-label">{String(resolvedLabel)}</div>
      )}
      <ul className="chart-tooltip-list">
        {entries.map((p, i) => (
          <li key={`${p.dataKey}-${i}`} className="chart-tooltip-row">
            <span
              className="chart-tooltip-swatch"
              style={{ background: (p.color as string) || (p.fill as string) || '#888' }}
              aria-hidden
            />
            {showName && (
              <span className="chart-tooltip-name">{p.name ?? String(p.dataKey)}</span>
            )}
            <span className="chart-tooltip-value">
              {fmt(p.value as number | string | undefined, p.name as string | undefined)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
